if (!customElements.get('mpc-video-dialog')) {
  customElements.define('mpc-video-dialog', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.dialog = this.querySelector('dialog');
      this.player = this.querySelector('[data-mpc-video-player]');
      const root = this.closest('[data-main-product-cro]');
      this.initPreviews(root, signal);

      root.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-mpc-video-open]');
        if (!trigger || this.dialog.open) return;
        const template = document.getElementById(trigger.dataset.mpcVideoOpen);
        if (!template || typeof this.dialog.showModal !== 'function') return;
        this.trigger = trigger;
        this.querySelector('[data-mpc-video-title]').textContent = trigger.dataset.videoTitle;
        this.player.replaceChildren(template.content.cloneNode(true));
        this.dialog.showModal();
        this.updatePreviews();
        this.querySelector('[data-mpc-video-close]').focus({ preventScroll: true });
        this.player.querySelector('video')?.play()?.catch(() => {
          // Native controls remain available when the browser refuses playback.
        });
      }, { signal });

      this.querySelector('[data-mpc-video-close]').addEventListener('click', () => this.dialog.close(), { signal });
      this.dialog.addEventListener('close', () => {
        this.stop();
        this.updatePreviews();
        if (this.trigger?.getClientRects().length) this.trigger.focus({ preventScroll: true });
      }, { signal });
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
          this.dialog.close();
        }
      }, { signal });
      window.matchMedia('(min-width: 769px)').addEventListener('change', () => {
        if (this.dialog.open) this.dialog.close();
      }, { signal });
    }

    initPreviews(root, signal) {
      this.previewObserver?.disconnect();
      this.interactionController?.abort();
      this.previews = [...root.querySelectorAll('[data-mpc-preview-src]')];
      this.previewToggles = [...root.querySelectorAll('[data-mpc-preview-toggle]')];
      this.visiblePreviews = new Set();
      this.failedPreviews = new Set();
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.previewsUnlocked = false;
      this.previewsPaused = false;
      this.interactionController = new AbortController();
      const unlock = (event) => {
        if (!event.isTrusted) return;
        this.previewsUnlocked = true;
        this.interactionController.abort();
        this.updatePreviews();
      };
      for (const type of ['pointerdown', 'keydown', 'wheel', 'touchstart']) {
        document.addEventListener(type, unlock, { passive: true, signal: this.interactionController.signal });
      }
      this.previewObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.25) this.visiblePreviews.add(entry.target);
          else this.visiblePreviews.delete(entry.target);
        }
        this.updatePreviews();
      }, { threshold: [0, 0.25] });
      for (const video of this.previews) {
        this.previewObserver.observe(video);
        video.addEventListener('playing', () => {
          video.classList.add('is-playing');
          for (const toggle of this.previewToggles) toggle.hidden = false;
        }, { signal });
      }
      for (const toggle of this.previewToggles) {
        toggle.addEventListener('click', () => {
          this.previewsPaused = !this.previewsPaused;
          for (const button of this.previewToggles) button.textContent = this.previewsPaused ? 'Resume previews' : 'Pause previews';
          this.updatePreviews();
        }, { signal });
      }
      document.addEventListener('visibilitychange', () => this.updatePreviews(), { signal });
      this.motion.addEventListener('change', () => this.updatePreviews(), { signal });
    }

    updatePreviews() {
      const allowed = this.previewsUnlocked && !this.previewsPaused && !this.motion.matches
        && !navigator.connection?.saveData && !document.hidden && !this.dialog.open;
      for (const video of this.previews) {
        if (!allowed || !this.visiblePreviews.has(video)) {
          video.pause();
          continue;
        }
        if (this.failedPreviews.has(video) || !video.paused) continue;
        video.muted = true;
        if (!video.hasAttribute('src')) video.src = video.dataset.mpcPreviewSrc;
        video.play()?.catch((error) => {
          if (error.name === 'AbortError') return;
          // Low Power Mode or browser policy can reject muted previews; keep the poster and modal trigger.
          this.failedPreviews.add(video);
          video.classList.remove('is-playing');
        });
      }
    }

    stop() {
      const video = this.player?.querySelector('video');
      if (!video) return;
      video.pause();
      video.removeAttribute('src');
      video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
      video.load();
      this.player.replaceChildren();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.interactionController?.abort();
      this.previewObserver?.disconnect();
      for (const video of this.previews ?? []) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
      if (this.dialog?.open) this.dialog.close();
      this.stop();
    }
  });
}
